import { Key } from "react";

import { RVGRID_COLUMN_SIZE_OPTIONS } from "../../consts";
import { RVGridCellAuditInfo } from "../../GridCells/RVGridCellAuditInfo/RVGridCellAuditInfo";

import type { FilterValue } from "antd/es/table/interface";

/**
 * Configuration contract for building a reusable audit grid column.
 */
type RVAuditColumnProps = {
    /** Column identifier, also reused for the data index. */
    key: string;
    /** Visible column title. */
    title: string;
    /** Dot-delimited path to the timestamp value on each row. */
    timestampField: string;
    /** Dot-delimited path to the user name value on each row. */
    userField: string;
    /** Optional AntD filter options for limiting by user. */
    filterOptions?: Array<{ text: string; value: string }>;
    /** Current filter state provided by the parent grid. */
    filterValue?: FilterValue;
};

/**
 * Resolves a nested property by walking a dot-delimited path (e.g. `createdInfo.createdBy`).
 */
function getNestedValue<T>(obj: T, path: string): any {
    return path
        .split(".")
        .reduce<unknown>(
            (acc, part) => (acc && typeof acc === "object" ? (acc as Record<string, unknown>)[part] : undefined),
            obj
        );
}

/**
 * Builds a standardized audit column definition with filtering, sorting, and `RVGridCellAuditInfo` rendering.
 */
export function RVAuditColumn<RecordType extends { id: string }>(props: RVAuditColumnProps) {
    const { key, title, timestampField, userField, filterOptions, filterValue } = props;

    return {
        key,
        dataIndex: key,
        title,
        size: RVGRID_COLUMN_SIZE_OPTIONS.SMALL,
        filters: filterOptions,
        filteredValue: filterValue,
        onFilter: (value: Key | boolean, record: RecordType) => {
            const fieldValue = getNestedValue(record, String(userField));
            // "null" is the shared emptyFilterOption sentinel; "" is used by some grids
            // (e.g. campaigns) for blank creators. Treat both as matching null/empty.
            if (value === "null" || value === "") {
                return fieldValue == null || fieldValue === "";
            }
            return fieldValue == value;
        },
        sorter: (a: RecordType, b: RecordType) => {
            const leftDate = new Date(getNestedValue(a, String(timestampField)));
            const rightDate = new Date(getNestedValue(b, String(timestampField)));
            return leftDate.getTime() - rightDate.getTime();
        },
        render(_: any, record: RecordType) {
            const auditInfo = {
                timestamp: getNestedValue(record, String(timestampField)),
                name: getNestedValue(record, String(userField)),
            };
            return <RVGridCellAuditInfo auditInfo={auditInfo} id={record.id} />;
        },
    };
}
